import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { NavbarDashbord } from './navbarDashbord';
import { ArrowLeft, Save, Image as ImageIcon, Plus, Trash2, Box, Info, Settings, Palette } from 'lucide-react';
import { EnregistrerClimatiseur } from './slice';
import '../styles/admin.css';

const TECH_PROPS_LIST = [
    "puissance", "surface", "type", "categorie", "Efficacité énergétique",
    "refrigerant", "garantie", "niveau sonore", "consommation", "poids", "gaz", "fonctionnalites"
];

export function AdminFormClimatiseur() {
    const { id } = useParams();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const listC = useSelector(state => state.listC) || [];

    const initialState = {
        id: Date.now(),
        markClimatisseur: '',
        nomClimatisseur: '',
        description: '',
        stock: 0,
        image: '', // Main preview image
        tailles: [
            {
                taille: '',
                prix: 0,
                details: { longueur: '', largeur: '', somk: '' },
                proprietesTechniques: TECH_PROPS_LIST.map(p => ({ propriete: p, valeur: '' }))
            }
        ],
        colors: [
            { color: '', image: '' }
        ],
        commantaires: []
    };

    const [formData, setFormData] = useState(initialState);

    useEffect(() => {
        if (id) {
            const item = listC.find(c => c.id.toString() === id.toString());
            if (item) {
                // Ensure existing data structure matches the new one
                const sanitizedItem = {
                    ...item,
                    tailles: item.tailles.map(t => ({
                        ...t,
                        proprietesTechniques: TECH_PROPS_LIST.map(pName => {
                            const existing = t.proprietesTechniques?.find(ep => ep.propriete === pName);
                            let val = existing ? existing.valeur : '';
                            // If value is an array (like functionalities), join it for the input
                            if (Array.isArray(val)) val = val.join(', ');
                            return { propriete: pName, valeur: val };
                        })
                    })),
                    colors: item.colors || item.couleurs || []
                };
                setFormData(sanitizedItem);
            }
        } else {
            setFormData(initialState);
        }
    }, [id, listC]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    // Taille Handlers
    const handleTailleChange = (index, field, value) => {
        const newTailles = formData.tailles.map((t, i) => {
            if (i !== index) return t;
            if (field.includes('.')) {
                const [parent, child] = field.split('.');
                return {
                    ...t,
                    [parent]: { ...t[parent], [child]: value }
                };
            }
            return { ...t, [field]: value };
        });
        setFormData({ ...formData, tailles: newTailles });
    };

    const handleTechPropChange = (tIndex, pIndex, value) => {
        const newTailles = formData.tailles.map((t, i) => {
            if (i !== tIndex) return t;
            const newProps = t.proprietesTechniques.map((p, pi) =>
                pi === pIndex ? { ...p, valeur: value } : p
            );
            return { ...t, proprietesTechniques: newProps };
        });
        setFormData({ ...formData, tailles: newTailles });
    };

    const addTaille = () => {
        setFormData({
            ...formData,
            tailles: [...formData.tailles, {
                taille: '',
                prix: 0,
                details: { longueur: '', largeur: '', somk: '' },
                proprietesTechniques: TECH_PROPS_LIST.map(p => ({ propriete: p, valeur: '' }))
            }]
        });
    };

    const removeTaille = (index) => {
        if (formData.tailles.length > 1) {
            setFormData({ ...formData, tailles: formData.tailles.filter((_, i) => i !== index) });
        }
    };

    // Color Handlers
    const handleColorChange = (index, field, value) => {
        const newColors = formData.colors.map((c, i) =>
            i === index ? { ...c, [field]: value } : c
        );
        setFormData({ ...formData, colors: newColors });
    };

    const addColor = () => {
        setFormData({
            ...formData,
            colors: [...formData.colors, { color: '', image: '' }]
        });
    };

    const removeColor = (index) => {
        if (formData.colors.length > 1) {
            setFormData({ ...formData, colors: formData.colors.filter((_, i) => i !== index) });
        }
    };

    const handleSave = (e) => {
        e.preventDefault();
        // Use the first color's image as the main product image if none is set
        const finalData = {
            ...formData,
            image: formData.image || (formData.colors[0]?.image || '')
        };
        dispatch(EnregistrerClimatiseur(finalData));
        alert(id ? 'Modification enregistrée !' : 'Nouveau produit ajouté !');
        navigate('/pageClimatisseurs');
    };

    return (
        <div className="admin-layout">
            <NavbarDashbord />
            <main className="admin-main-content">
                <header className="admin-header">
                    <div className="admin-title-group">
                        <button onClick={() => navigate(-1)} className="admin-back-btn">
                            <ArrowLeft size={18} /> <span>Retour</span>
                        </button>
                        <h1>{id ? 'Modifier Climatiseur' : 'Nouveau Climatiseur'}</h1>
                    </div>
                    <button onClick={handleSave} className="admin-btn-save">
                        <Save size={18} /> <span>{id ? 'Mettre à jour' : 'Publier'}</span>
                    </button>
                </header>

                <div className="admin-form-container">
                    <form className="admin-main-form" onSubmit={handleSave}>
                        <div className="form-sections-grid">

                            {/* Column 1: Basic & Colors */}
                            <div className="form-column">
                                <section className="form-section-card">
                                    <div className="section-header">
                                        <Info size={20} color="var(--admin-accent)" />
                                        <h3>Informations Générales</h3>
                                    </div>
                                    <div className="form-group-row">
                                        <div className="form-group">
                                            <label>Marque</label>
                                            <input type="text" name="markClimatisseur" value={formData.markClimatisseur} onChange={handleChange} placeholder="Ex: LG" required />
                                        </div>
                                        <div className="form-group">
                                            <label>Modèle</label>
                                            <input type="text" name="nomClimatisseur" value={formData.nomClimatisseur} onChange={handleChange} placeholder="Ex: ARTCOOL Mirror" required />
                                        </div>
                                        <div className="form-group">
                                            <label>Stock Initial</label>
                                            <input type="number" name="stock" value={formData.stock || 0} onChange={handleChange} required />
                                        </div>
                                    </div>
                                    <div className="form-group">
                                        <label>Description</label>
                                        <textarea name="description" value={formData.description} onChange={handleChange} rows="4" placeholder="Description marketing..." required />
                                    </div>
                                </section>

                                <section className="form-section-card">
                                    <div className="section-header">
                                        <Palette size={20} color="var(--admin-accent)" />
                                        <div style={{ flex: 1 }}>
                                            <h3>Nuancier & Visuels</h3>
                                            <p style={{ margin: 0, fontSize: '0.75rem', color: 'var(--admin-text-muted)' }}>Associez une image à chaque couleur</p>
                                        </div>
                                        <button type="button" className="btn-add-variant" onClick={addColor}>
                                            <Plus size={16} /> Couleur
                                        </button>
                                    </div>
                                    <div className="colors-list">
                                        {formData.colors.map((c, index) => (
                                            <div key={index} className="variant-item-card" style={{ marginBottom: '20px', position: 'relative' }}>
                                                {formData.colors.length > 1 && (
                                                    <button
                                                        type="button"
                                                        className="btn-remove-lite"
                                                        onClick={() => removeColor(index)}
                                                        style={{ position: 'absolute', top: '15px', right: '15px', zIndex: 1 }}
                                                        title="Supprimer cette couleur"
                                                    >
                                                        <Trash2 size={16} />
                                                    </button>
                                                )}
                                                <div className="variant-card-header">
                                                    <strong>COULEUR #{index + 1}</strong>
                                                </div>

                                                <div className="form-group-row" style={{ gridTemplateColumns: '1fr 2fr', alignItems: 'center', marginTop: '15px' }}>
                                                    <div className="form-group" style={{ marginBottom: '0' }}>
                                                        <label>Couleur</label>
                                                        <input type="text" value={c.color} onChange={(e) => handleColorChange(index, 'color', e.target.value)} placeholder="Ex: Blanc" required />
                                                    </div>
                                                    <div className="form-group" style={{ marginBottom: '0' }}>
                                                        <label>URL Image spécifique</label>
                                                        <input type="text" name="image" value={c.image} onChange={(e) => handleColorChange(index, 'image', e.target.value)} placeholder="https://..." required />
                                                    </div>
                                                </div>

                                                {c.image && (
                                                    <div className="color-preview-container" style={{ width: '99%' }}>
                                                        <img src={c.image} alt="Color preview" className="color-preview-img" />
                                                    </div>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                </section>
                            </div>

                            {/* Column 2: Variants (Tailles) */}
                            <div className="form-column">
                                <section className="form-section-card">
                                    <div className="section-header">
                                        <Box size={20} color="var(--admin-accent)" />
                                        <div style={{ flex: 1 }}>
                                            <h3>Versions & Spécifications</h3>
                                            <p style={{ margin: 0, fontSize: '0.75rem', color: 'var(--admin-text-muted)' }}>Puissance, Prix et Fiche Technique</p>
                                        </div>
                                        <button type="button" className="btn-add-variant" onClick={addTaille}>
                                            <Plus size={16} /> Variante
                                        </button>
                                    </div>

                                    <div className="tailles-list">
                                        {formData.tailles.map((t, tIndex) => (
                                            <div key={tIndex} className="variant-item-card" style={{ position: 'relative' }}>
                                                {formData.tailles.length > 1 && (
                                                    <button
                                                        type="button"
                                                        className="btn-remove-lite"
                                                        onClick={() => removeTaille(tIndex)}
                                                        style={{ position: 'absolute', top: '15px', right: '15px', zIndex: 1 }}
                                                        title="Supprimer cette variante"
                                                    >
                                                        <Trash2 size={16} />
                                                    </button>
                                                )}
                                                <div className="variant-card-header">
                                                    <strong>VERSION #{tIndex + 1}</strong>
                                                </div>
                                                <div className="form-group-row">
                                                    <div className="form-group">
                                                        <label>Taille/Puissance</label>
                                                        <input type="text" value={t.taille} onChange={(e) => handleTailleChange(tIndex, 'taille', e.target.value)} placeholder="Ex: 12000 BTU" required />
                                                    </div>
                                                    <div className="form-group">
                                                        <label>Prix (MAD)</label>
                                                        <input type="number" value={t.prix} onChange={(e) => handleTailleChange(tIndex, 'prix', parseInt(e.target.value))} required />
                                                    </div>
                                                </div>

                                                <div style={{ background: 'rgba(99, 102, 241, 0.03)', padding: '15px', borderRadius: '12px', margin: '15px 0' }}>
                                                    <label style={{ fontSize: '0.7rem', fontWeight: '900', color: 'var(--admin-accent)', display: 'block', marginBottom: '10px' }}>DIMENSIONS (DETAIL)</label>
                                                    <div className="form-group-grid-3">
                                                        <div className="form-group">
                                                            <label>Long.</label>
                                                            <input type="text" value={t.details.longueur} onChange={(e) => handleTailleChange(tIndex, 'details.longueur', e.target.value)} placeholder="00cm" />
                                                        </div>
                                                        <div className="form-group">
                                                            <label>Larg.</label>
                                                            <input type="text" value={t.details.largeur} onChange={(e) => handleTailleChange(tIndex, 'details.largeur', e.target.value)} placeholder="00cm" />
                                                        </div>
                                                        <div className="form-group">
                                                            <label>Somk.</label>
                                                            <input type="text" value={t.details.somk} onChange={(e) => handleTailleChange(tIndex, 'details.somk', e.target.value)} placeholder="00cm" />
                                                        </div>
                                                    </div>
                                                </div>

                                                <div className="technical-props-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                                                    <label style={{ gridColumn: '1/3', fontSize: '0.7rem', fontWeight: '900', color: 'var(--admin-accent)', marginBottom: '5px' }}>PROPRIÉTÉS TECHNIQUES</label>
                                                    {t.proprietesTechniques.map((p, pIndex) => (
                                                        <div key={pIndex} className="form-group" style={{ marginBottom: '5px' }}>
                                                            <label style={{ fontSize: '0.65rem', textTransform: 'capitalize' }}>{p.propriete}</label>
                                                            <input
                                                                type="text"
                                                                value={p.valeur}
                                                                onChange={(e) => handleTechPropChange(tIndex, pIndex, e.target.value)}
                                                                style={{ padding: '8px 12px', fontSize: '0.8rem' }}
                                                            />
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </section>
                            </div>

                        </div>
                    </form>
                </div>
            </main>
        </div>
    );
}
